import { useState } from "react";
import { Shell } from "../../components/Shell";
import { ActionMenu, Button, Empty, ErrorNotice, Field, Input, Loading, PageHead, Panel, StatusBadge, useToast } from "../../components/ui";
import { developerApi } from "../../lib/api";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { Link, navigate, useSearchParam } from "../../lib/router";
import { useClient, useSession } from "../../lib/session";
import { formatDateTime } from "../../lib/time";
import type { Game } from "../../lib/types";
import { DeleteGameDialog, GameStatusDialog } from "./GameSettings";

export function DevGames() {
  const api = developerApi(useClient());
  const { session } = useSession();
  const toast = useToast();
  const games = useQuery("dev:games", () => api.games());
  const [name, setName] = useState("");
  const create = useAction(() => api.createGame(name.trim()));
  const [welcome, setWelcome] = useSearchParam("welcome");
  const noGames = games.data?.data.length === 0;
  const [acting, setActing] = useState<{ game: Game; action: "status" | "delete" } | null>(null);

  return (
    <Shell crumbs={[{ label: "Your games" }]}>
      <PageHead title={`Welcome, ${session?.name}`} lede="Each game gets its own banks, keys and clock, with a sandbox to try everything before going live." />
      <div className="stack">
        {(welcome || noGames) && (
          <Panel
            title={welcome ? "Your account is ready" : "Start with your first game"}
            actions={welcome ? <Button size="small" variant="ghost" onClick={() => setWelcome("")}>Dismiss</Button> : undefined}
          >
            <ol className="muted" style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 6 }}>
              <li><strong>Create a game</strong> below. It gets a sandbox and a production environment.</li>
              <li>Follow the game's <strong>Getting started</strong> checklist: create a sandbox bank and issue an API key for your game server.</li>
              <li>Move money in the <strong>sandbox playground</strong>, then go live with production keys.</li>
            </ol>
          </Panel>
        )}
        <Panel title={noGames ? "Create your first game" : "Create a game"}>
          <form
            className="row end"
            onSubmit={async (e) => {
              e.preventDefault();
              const g = await create.run();
              if (g) {
                setName("");
                invalidate("dev:games");
                toast(`Created ${g.name}`);
                navigate(`/dev/games/${g.id}`);
              }
            }}
          >
            <div className="grow" style={{ maxWidth: 420 }}>
              <Field label="Game name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Lagos Hustle" required maxLength={120} autoFocus={!!welcome} />
              </Field>
            </div>
            <Button type="submit" variant="primary" busy={create.pending} disabled={!name.trim()}>Create game</Button>
          </form>
          <div style={{ marginTop: 12 }}><ErrorNotice error={create.error} /></div>
        </Panel>
        <Panel title="Your games" flush>
          <ErrorNotice error={games.error} onRetry={games.reload} />
          {games.loading && !games.data && <Loading />}
          {games.data && games.data.data.length === 0 && <Empty title="No games yet">Create your first game above to get a sandbox.</Empty>}
          {games.data && games.data.data.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead><tr><th>Game</th><th>Status</th><th>Created</th><th><span className="sr-only">Actions</span></th></tr></thead>
                <tbody>
                  {games.data.data.map((g) => (
                    <tr key={g.id} className="clickable" onClick={() => navigate(`/dev/games/${g.id}`)}>
                      <td>
                        <Link to={`/dev/games/${g.id}`} className="cell-title" onClick={(e) => e.stopPropagation()}>{g.name}</Link>
                        <div className="cell-sub mono">{g.slug}</div>
                      </td>
                      <td><StatusBadge status={g.status} /></td>
                      <td>{formatDateTime(g.created_at)}</td>
                      <td className="num" onClick={(e) => e.stopPropagation()}>
                        <ActionMenu label={`Actions for ${g.name}`} items={[
                          { label: "Open sandbox", onSelect: () => navigate(`/dev/games/${g.id}/sandbox`) },
                          { label: g.status === "active" ? "Suspend game" : "Reactivate game", onSelect: () => setActing({ game: g, action: "status" }) },
                          { label: "Delete game", danger: true, onSelect: () => setActing({ game: g, action: "delete" }) },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {acting?.action === "status" && <GameStatusDialog key={acting.game.id} game={acting.game} open onClose={() => setActing(null)} />}
          {acting?.action === "delete" && <DeleteGameDialog key={acting.game.id} game={acting.game} open onClose={() => setActing(null)} />}
        </Panel>
      </div>
    </Shell>
  );
}
